<template>
  <div>
    <dummy-register-page v-show="is_reg_locked" />
    <z-form
      v-show="!acc_configured && !is_reg_locked"
      ref="form"
      v-slot="{ errors }"
      autocomplete="on"
      @submit.native.prevent="handler_submit"
    >
      <backend-error />

      <base-form
        v-show="!verification_step"
        ref="base_form"
        v-model="form"
        :is_init="is_init"
        @apply_form="validate_and_send_sms"
      />

      <verify-code
        ref="verify_code"
        :phone_format="format_phone"
        :limit_error="limit_error"
        :trigger="trigger"
        @change_phone="change_phone"
        @error="handler_error"
        @remove_data="remove_data"
        @success_data="success_data"
        @change_step="change_step"
        @timer:restart="validate_and_send_sms"
      />

      <auth-link v-show="!verification_step" />
    </z-form>

    <div v-show="acc_setting" class="b-acc-configured">
      <span v-html="$t('core.register.confirm.acc_configured')"></span>
      <img class="rotate" src="/img/icons/loader.png" alt="" />
    </div>

    <div v-show="events_fail" class="b-acc-configured large">
      <span v-html="$t('core.register.events.fail')"></span>
    </div>

    <div v-if="events_timeout" class="b-acc-configured large">
      <span v-html="$t('core.register.events.timeout')"></span>
    </div>
  </div>
</template>

<script>
  import AuthLink from './register-form-auth-link';
  import BackendError from '../auth-backend-error';
  import BaseForm from '@core/components/auth/register-form/register-form-base-form';
  import VerifyCode from './register-form-verify-code';
  import jwt_decode from 'jwt-decode';
  import io from 'socket.io-client';
  import axios from 'axios';
  import { Currency } from '@shared/helpers/currency';
  import { PageServerValidationResponseMixin } from '@shared/mixins/page-server-validation-response';
  import { RegisterService } from '@core/services/RegisterService';
  import { StateMixin } from '@core/components/auth/register-form/register-form.mixin';
  import { Storage } from '@app/plugins/storage';
  import { Lang } from '@shared/helpers/lang';
  import { SmsVerificationService } from '@core/services/SmsVerificationService';
  import { API_URL, NOTIFY_SOCKET_IO, RECAPTCHA_SITE_KEY } from '@shared/helpers/env';
  import { captcha } from '@utils/captcha';
  import DummyRegisterPage from '@core/pages/dummy-register-page';
  import { AuthService } from '@core/services/AuthService';
  import encrypt from '@utils/encrypt';

  const register_form_lc_key = 'register_form';
  const TIMER = 3 * 6 * 10000;
  const ERROR_1001 = 1001;
  const ERROR_1002 = 1002;

  export default {
    components: { BaseForm, BackendError, AuthLink, VerifyCode, DummyRegisterPage },
    mixins: [PageServerValidationResponseMixin, StateMixin],
    data: () => ({
      limit_error: 0,
      verification_step: false,
      acc_configured: false,
      acc_setting: false,
      events_fail: false,
      events_timeout: false,
      user_id: '',
      user_token: '',
      user_key: '',
      ws_connection: null,
      headers: null,
      timeout_status_request: null,
      trigger: null,
      is_reg_locked: true,
      form: {
        email: '',
        phone: '',
        currency_id: Currency.USD_ID,
        rights: false,
        ref_code: ''
      },
      recaptchaScript: null,
      csrf_token: ''
    }),

    computed: {
      format_phone() {
        // backend await phone number without +
        return this.form.phone.replace(/[^\d.]/gi, '');
      }
    },

    created() {
      this.ws_connection = io.connect(NOTIFY_SOCKET_IO, {
        reconnection: true,
        reconnectionDelay: 1000,
        reconnectionAttempts: 10,
        withCredentials: false
      });
    },

    beforeDestroy() {
      this.recaptchaScript.remove();
    },

    mounted() {
      if (this.$route.params.verify_code) {
        this.form.ref_code = this.$route.params.verify_code;
      }

      this.recaptchaScript = document.createElement('script');
      this.recaptchaScript.setAttribute(
        'src',
        `https://www.google.com/recaptcha/api.js?render=${RECAPTCHA_SITE_KEY}`
      );
      document.head.appendChild(this.recaptchaScript);
    },

    methods: {
      // Сохранение данных в LC
      save_data() {
        Storage.set(register_form_lc_key, this.form);
      },

      //
      success_data(code = '') {
        this.form.code = code;
        this.save_data();
        this.handle_register();
      },

      //
      change_step() {
        this.verification_step = true;
      },

      // Удаление данных из LC
      remove_data() {
        Storage.remove(register_form_lc_key);
      },

      // Запрос регистрации
      handle_register() {
        const { email, code, ref_code, currency_id } = this.form;
        const phone = this.format_phone;
        const locale = this.$i18n.locale || Lang.getDefault();
        const params = { email, code, phone, currency_id, ref_code, locale };

        captcha('registration', (token) => {
          RegisterService.register({ ...params, token })
            .then((resp) => {
              if (resp.message === 'bad_request') {
                return this.$noty.error(this.$t('core.errors.captcha_bad_request'));
              } else if (resp) {
                this.remove_data();
                this.verification_step = false;
                this.acc_configured = true;
                this.acc_setting = true;
                this.account_setup(resp);
              } else {
                this.$noty.error(this.$t('core.errors.general_not_success_request'));
              }
            })
            .catch((e) => {
              this.handler_error(e);
              this.remove_data();
            });
        });
      },

      check_status_registration() {
        axios
          .get(`${API_URL}/api/v2/notify/notifications`, {
            headers: {
              Authorization: this.headers
            }
          })
          .then((resp) => {
            const status = this.$R.pathOr('', ['data', 0, 'type'], resp.data);

            if (status === 'event_registration_success') {
              this.ws_connection.close();
              localStorage.firstEntry = true;
              localStorage.userKey = this.user_key;

              return this.$router.push({
                name: 'dashboard',
                query: { id: this.user_id, token: this.user_token }
              });
            } else if (status === 'event_registration_fail') {
              this.ws_connection.close();
              this.events_fail = true;
              this.acc_setting = false;
            } else {
              this.ws_connection.close();
              this.events_timeout = true;
              this.acc_setting = false;
            }
          })
          .catch((e) => {
            console.log('check_registration_status', e);
          });
      },

      account_setup(resp) {
        this.user_token = this.$R.pathOr('', ['data', 'token', 'access_token'], resp);
        this.user_key = this.$R.pathOr('', ['data', 'user_key'], resp);
        this.user_id = jwt_decode(this.user_token).sub;
        this.headers = `Bearer ${this.user_token}`;
        let timeout = null;
        const unsubscribe = () => {
          this.ws_connection.close();
          clearTimeout(this.timeout_status_request);
          clearTimeout(timeout);
        };

        setTimeout(() => {
          this.check_status_registration();
        }, TIMER);

        this.ws_connection.on(`zrr.notify.notifications:${this.user_key}`, (payload) => {
          const status = this.$R.pathOr('', ['data', 'type'], payload);

          if (status === 'event_registration_success') {
            unsubscribe();
            localStorage.firstEntry = true;

            return this.$router.push({
              name: 'dashboard',
              query: { id: this.user_id, token: this.user_token }
            });
          } else if (status === 'event_registration_fail') {
            this.events_fail = true;
            this.acc_setting = false;
            unsubscribe();
          } else {
            this.events_timeout = true;
            this.acc_setting = false;
            unsubscribe();
          }
        });
      },

      async handler_submit() {
        if (this.is_validated_phone) {
          try {
            const is_valid = await this.$refs.form.validate();
            if (is_valid) {
              this.handle_register();
            }
          } catch (e) {
            console.error(e);
          }
        } else {
          this.validate_email_phone();
        }
      },

      change_phone() {
        this.verification_step = false;
        this.$refs.verify_code.set_init();
        this.$refs.base_form.focus_phone();
      },

      validate_and_send_sms() {
        this.validate_email_phone()
          .then(() => {
            AuthService.token().then((response) => {
              this.csrf_token = response.headers['xsrf-token'];
              captcha('registration_verify', this.send_sms_verification);
            });
          })
          .catch(() => {});
      },

      async validate_email_phone() {
        return new Promise(async (resolve, reject) => {
          try {
            const is_valid = await this.$refs.base_form.validate();

            if (is_valid) {
              const { email } = this.form;
              const phone = this.format_phone;
              const params = { email, phone };
              this.$wait.start('register:check_email_phone');
              RegisterService.checkEmailPhone(params)
                .then(() => {
                  this.save_data();
                  resolve();
                })
                .catch((e) => {
                  this.remove_data();
                  this.handler_error(e);
                  reject();
                })
                .finally(() => {
                  this.$wait.end('register:check_email_phone');
                });
            }
          } catch (e) {
            console.error(e);
            this.handler_error(e);
          }
        });
      },

      // Запрос на отправку смс
      send_sms_verification(token) {
        const params = {
          number: this.format_phone,
          token
        };

        SmsVerificationService.send(encrypt(this.csrf_token), params)
          .then((response) => {
            if (response.message === 'bad_request') {
              this.$noty.error(this.$t('core.errors.captcha_bad_request'));
            } else {
              this.$refs.verify_code.start_verify();
            }
          })
          .catch((e) => {
            if (e.response.status == 423) {
              return (this.is_reg_locked = true);
            } else {
              this.is_reg_locked = false;
            }
            if (this.verification_step && e.response.status == 429) {
              this.limit_error = ERROR_1001;
              this.trigger = Math.random();
            } else if (
              this.verification_step &&
              (e.response?.data?.code === ERROR_1001 || e.response?.data?.code === ERROR_1002)
            ) {
              this.limit_error = e.response?.data?.code;
              this.trigger = Math.random();
            }
            this.handler_error(e);
          });
      },

      // Глобальные обработчик ошибок
      handler_error(error_data) {
        if (error_data) {
          const errorEmailMessage = this.$R.pathOr(
            '',
            ['response', 'data', 'errors', 'email'],
            error_data
          );
          const errorPhoneMessage = this.$R.pathOr(
            '',
            ['response', 'data', 'errors', 'phone'],
            error_data
          );

          if (error_data?.response) {
            if (error_data?.response?.status === 429) {
              return this.handler_error_code(ERROR_1001);
            }
            if (error_data?.response?.data?.code) {
              this.handler_error_code(error_data?.response?.data?.code);
            } else if (error_data?.response?.data?.errors) {
              this.check_server_validation(error_data);
            } else {
              this.show_something_went_wrong();
            }
          } else if (errorEmailMessage) {
            this.show_something_went_wrong(errorEmailMessage);
          } else if (errorPhoneMessage) {
            this.show_something_went_wrong(errorPhoneMessage);
          } else {
            this.show_something_went_wrong();
          }
        } else {
          return;
        }
      },

      // Обработка error response с ключом code
      handler_error_code(code) {
        const locale_key = `core.register.error.${code}`;

        if (this.$te(locale_key)) {
          const error_message = {
            custom: this.$t(locale_key)
          };
          this.show_custom_errors(error_message);
        } else {
          this.show_something_went_wrong();
        }
      },

      // Показать необработанные ошибки
      show_something_went_wrong(field = '') {
        this.show_custom_errors({
          custom:
            field === ''
              ? this.$t('core.register.error.something_went_wrong')
              : this.$t(`core.register.error.${field}`)
        });
      },

      // Показать кастомные ошибки для формы
      show_custom_errors(e) {
        const error_msg = this.$R.pathOr('', ['custom'], e);

        if (this.$refs.form.setErrors) {
          this.$noty.error(error_msg);
          this.$refs.form.setErrors(e);
        }
      }
    }
  };
</script>

<style lang="scss">
  .auth-content__form__submit:disabled {
    background: #969faa;
    border-color: #969faa;
  }

  .b-acc-configured {
    color: #fff;
    font-family: 'Lato', 'sans-serif';
    font-size: 17px;
    line-height: 34px;
    width: 300px;
    text-align: center;
    margin: 150px auto auto;

    &.large {
      width: 500px;
      margin-left: -70px;
    }

    img.rotate {
      animation: 2s linear 0s normal none infinite running rotate;
      -webkit-animation: 2s linear 0s normal none infinite running rotate;
      width: 70px;
      height: 70px;
      display: block;
      margin: 40px auto 0;
    }

    @keyframes rotate {
      0% {
        transform: rotate(0deg);
      }
      100% {
        transform: rotate(360deg);
      }
    }
    @-webkit-keyframes rotate {
      0% {
        transform: rotate(0deg);
      }
      100% {
        transform: rotate(360deg);
      }
    }
  }

  .white {
    text-align: center;
    color: #fff;
    font-size: 16px;
  }

  .layout--auth .auth-content {
    max-width: 400px;
    // max-width: 530px;
  }
</style>
